import { map } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { inject, Injectable } from '@angular/core';

import {
  isAllOf,
  isAnyOf,
  isWoPermission,
  isRole,
  PermissionTokens,
  PermissionToken,
  isRoleType,
} from './auth.types';
import { WO_USER_PERMISSIONS, WO_USER_ROLES } from './auth.config';

import { isRoleOfType } from '@iss/libs/permissions';
import { Role, WoPermission } from '@iss/libs/graphql';

@Injectable()
export class PermissionsService {
  private readonly roles$ = inject(WO_USER_ROLES);
  private readonly permissions$ = inject(WO_USER_PERMISSIONS);

  public isSufficient(permissions: PermissionTokens): Observable<boolean> {
    return combineLatest([ this.roles$, this.permissions$ ]).pipe(
      map(([ availableRoles, availablePermissions ]) => this.isMatched(availableRoles, availablePermissions, permissions)),
    );
  }

  private isMatched(availableRoles: Set<Role>, availablePermissions: Set<WoPermission>, permissions: PermissionTokens): boolean {
    if (permissions === '*') {
      return true;
    }

    if (isAnyOf(permissions)) {
      return permissions.anyOf.some(permission => this.has(availableRoles, availablePermissions, permission));
    }

    if (isAllOf(permissions)) {
      return permissions.allOf.every(permission => this.has(availableRoles, availablePermissions, permission));
    }

    return this.has(availableRoles, availablePermissions, permissions);
  }

  private has(availableRoles: Set<Role>, availablePermissions: Set<WoPermission>, token: PermissionToken): boolean {
    return isRole(token)
      ? availableRoles.has(token)
      : isWoPermission(token)
        ? availablePermissions.has(token)
        : isRoleType(token)
          ? [ ...availableRoles.values() ].some(role => isRoleOfType(role, token))
          : false;
  }
}
